import React from 'react';
import { GraduationCap } from 'lucide-react';
import WebBasedCourse from '../CreateCourseTab/WebBasedCourse';
import ILTCourse from '../CreateCourseTab/ILTCourse';
import BlendedCourse from '../CreateCourseTab/BlendedCourse';
import './CourseCreationTabs.css';

// Import types for modules, offerings, and sessions
export interface SelectedModule {
  id: number;
  title: string;
  type: string;
  duration: string;
  contentType?: 'document' | 'scorm' | 'mp4' | 'url';
  file?: File | null;
  url?: string;
}

export interface Offering {
  id: number;
  name: string;
  location: string;
  startDate: string;
  endDate: string;
  timezone: string;
  totalSeats: number;
  enableWaitlist: boolean;
}

export interface Session {
  id: number;
  name: string;
  date: string;
  startTime: string;
  endTime: string;
  instructor: string;
  deliveryMethod: string;
  locationRoom: string;
  virtualMeetingLink: string;
}

export interface CourseContentTabProps {
  courseType: 'web' | 'ilt' | 'blended';

  // Web-based / Blended - Modules
  selectedModules: SelectedModule[];
  onOpenModulesModal: () => void;
  onRemoveModule: (moduleId: number) => void;
  onAddModule: (module: SelectedModule) => void;

  // ILT - Offerings
  offerings: Offering[];
  showOfferingForm: boolean;
  expandedOffering: number | null;
  offeringName: string;
  offeringLocation: string;
  offeringStartDate: string;
  offeringEndDate: string;
  offeringTimezone: string;
  offeringSeats: string;
  enableWaitlist: boolean;
  onShowOfferingFormToggle: () => void;
  onExpandedOfferingToggle: (id: number | null) => void;
  onOfferingNameChange: (value: string) => void;
  onOfferingLocationChange: (value: string) => void;
  onOfferingStartDateChange: (value: string) => void;
  onOfferingEndDateChange: (value: string) => void;
  onOfferingTimezoneChange: (value: string) => void;
  onOfferingSeatsChange: (value: string) => void;
  onEnableWaitlistChange: (value: boolean) => void;
  onAddOffering: () => void;
  onDeleteOffering: (id: number) => void;

  // ILT - Sessions
  sessions: Session[];
  showSessionForm: boolean;
  sessionDate: string;
  sessionStartTime: string;
  sessionEndTime: string;
  sessionInstructor: string;
  sessionDeliveryMethod: string;
  sessionLocationRoom: string;
  sessionVirtualLink: string;
  onShowSessionFormToggle: () => void;
  onSessionDateChange: (value: string) => void;
  onSessionStartTimeChange: (value: string) => void;
  onSessionEndTimeChange: (value: string) => void;
  onSessionInstructorChange: (value: string) => void;
  onSessionDeliveryMethodChange: (value: string) => void;
  onSessionLocationRoomChange: (value: string) => void;
  onSessionVirtualLinkChange: (value: string) => void;
  onAddSession: () => void;
  onDeleteSession: (id: number) => void;

  validationErrors: Record<string, string>;
}

export const CourseContentTab: React.FC<CourseContentTabProps> = ({
  courseType,
  selectedModules,
  onOpenModulesModal,
  onRemoveModule,
  onAddModule,
  offerings,
  showOfferingForm,
  expandedOffering,
  offeringName,
  offeringLocation,
  offeringStartDate,
  offeringEndDate,
  offeringTimezone,
  offeringSeats,
  enableWaitlist,
  onShowOfferingFormToggle,
  onExpandedOfferingToggle,
  onOfferingNameChange,
  onOfferingLocationChange,
  onOfferingStartDateChange,
  onOfferingEndDateChange,
  onOfferingTimezoneChange,
  onOfferingSeatsChange,
  onEnableWaitlistChange,
  onAddOffering,
  onDeleteOffering,
  sessions,
  showSessionForm,
  sessionDate,
  sessionStartTime,
  sessionEndTime,
  sessionInstructor,
  sessionDeliveryMethod,
  sessionLocationRoom,
  sessionVirtualLink,
  onShowSessionFormToggle,
  onSessionDateChange,
  onSessionStartTimeChange,
  onSessionEndTimeChange,
  onSessionInstructorChange,
  onSessionDeliveryMethodChange,
  onSessionLocationRoomChange,
  onSessionVirtualLinkChange,
  onAddSession,
  onDeleteSession,
  validationErrors,
}) => {
  const getHeaderContent = () => {
    switch (courseType) {
      case 'web':
        return {
          title: 'Course Modules',
          description: 'Add learning modules and materials for your web-based course',
          color: '#3b82f6',
        };
      case 'ilt':
        return {
          title: 'Offerings & Sessions',
          description: 'Schedule instructor-led training sessions and offerings',
          color: '#059669',
        };
      case 'blended':
        return {
          title: 'Course Content',
          description: 'Combine online modules with instructor-led sessions',
          color: '#8b5cf6',
        };
      default:
        return {
          title: 'Course Content',
          description: 'Add content to your course',
          color: '#FF8C00',
        };
    }
  };

  const headerContent = getHeaderContent();

  return (
    <div className="tab-content">
      {/* Section Header */}
      <div className="section-header content-header">
        <h3>
          <GraduationCap size={20} />
          {headerContent.title}
        </h3>
        <p>{headerContent.description}</p>
      </div>

      {/* Render content based on course type */}
      {courseType === 'web' && (
        <WebBasedCourse
          selectedModules={selectedModules}
          onOpenModulesModal={onOpenModulesModal}
          onRemoveModule={onRemoveModule}
          onAddModule={onAddModule}
        />
      )}

      {courseType === 'ilt' && (
        <ILTCourse
          offerings={offerings}
          showOfferingForm={showOfferingForm}
          expandedOffering={expandedOffering}
          offeringName={offeringName}
          offeringLocation={offeringLocation}
          offeringStartDate={offeringStartDate}
          offeringEndDate={offeringEndDate}
          offeringTimezone={offeringTimezone}
          offeringSeats={offeringSeats}
          enableWaitlist={enableWaitlist}
          onShowOfferingFormToggle={onShowOfferingFormToggle}
          onExpandedOfferingToggle={onExpandedOfferingToggle}
          onOfferingNameChange={onOfferingNameChange}
          onOfferingLocationChange={onOfferingLocationChange}
          onOfferingStartDateChange={onOfferingStartDateChange}
          onOfferingEndDateChange={onOfferingEndDateChange}
          onOfferingTimezoneChange={onOfferingTimezoneChange}
          onOfferingSeatsChange={onOfferingSeatsChange}
          onEnableWaitlistChange={onEnableWaitlistChange}
          onAddOffering={onAddOffering}
          onDeleteOffering={onDeleteOffering}
          sessions={sessions}
          showSessionForm={showSessionForm}
          sessionDate={sessionDate}
          sessionStartTime={sessionStartTime}
          sessionEndTime={sessionEndTime}
          sessionInstructor={sessionInstructor}
          sessionDeliveryMethod={sessionDeliveryMethod}
          sessionLocationRoom={sessionLocationRoom}
          sessionVirtualLink={sessionVirtualLink}
          onShowSessionFormToggle={onShowSessionFormToggle}
          onSessionDateChange={onSessionDateChange}
          onSessionStartTimeChange={onSessionStartTimeChange}
          onSessionEndTimeChange={onSessionEndTimeChange}
          onSessionInstructorChange={onSessionInstructorChange}
          onSessionDeliveryMethodChange={onSessionDeliveryMethodChange}
          onSessionLocationRoomChange={onSessionLocationRoomChange}
          onSessionVirtualLinkChange={onSessionVirtualLinkChange}
          onAddSession={onAddSession}
          onDeleteSession={onDeleteSession}
        />
      )}

      {courseType === 'blended' && (
        <BlendedCourse
          selectedModules={selectedModules}
          onOpenModulesModal={onOpenModulesModal}
          onRemoveModule={onRemoveModule}
          onAddModule={onAddModule}
        />
      )}
    </div>
  );
};
